/**
 * Block Registry
 *
 * 블록 타입과 React 컴포넌트 간의 매핑을 관리하는 레지스트리
 *
 * @design-principles
 * 1. Open/Closed: 새 블록 추가 시 레지스트리 등록만으로 확장
 * 2. Pragmatic Types: 런타임 유연성을 위한 실용적 타입 설계
 * 3. Lazy Loading: 동적 import 지원으로 번들 최적화
 */

import type { ComponentType } from 'react';
import type { Block, BlockType } from '@/shared/types/blocks';

// ============================================================================
// TYPES
// ============================================================================

/**
 * 블록 렌더러에 전달되는 공통 props
 */
export interface BlockRendererContext {
  /** 도구 클릭 핸들러 (TechStackBlock 등에서 사용) */
  onToolClick?: (toolId: string) => void;
  /** 재귀 렌더링 함수 (컨테이너 블록용) */
  renderBlock?: (block: Block) => React.ReactNode;
  /** 연결된 YouTube 영상 ID (video-info + transcript 레이아웃용) */
  linkedVideoId?: string;
}

/**
 * 블록 컴포넌트 타입 (유연한 props 허용)
 */
// biome-ignore lint/suspicious/noExplicitAny: 기존 컴포넌트와의 호환성을 위해 유연한 타입 사용
export type BlockComponent = ComponentType<any>;

/**
 * 블록 등록 정보
 */
interface BlockRegistration {
  /** 블록 컴포넌트 */
  component: BlockComponent;
  /** 컴포넌트 표시 이름 (디버깅용) */
  displayName?: string;
  /** 블록 카테고리 */
  category?: 'layout' | 'content' | 'data' | 'interactive' | 'media';
}

/**
 * 레지스트리 타입
 */
type BlockRegistry = Partial<Record<BlockType, BlockRegistration>>;

// ============================================================================
// REGISTRY
// ============================================================================

/**
 * 블록 레지스트리 인스턴스
 * 모듈 스코프에서 싱글톤으로 관리
 */
const registry: BlockRegistry = {};

/**
 * 블록 컴포넌트 등록
 *
 * @example
 * ```typescript
 * registerBlock('heading', HeadingBlock, {
 *   displayName: 'HeadingBlock',
 *   category: 'content',
 * });
 * ```
 */
export function registerBlock(
  type: BlockType,
  component: BlockComponent,
  options?: Omit<BlockRegistration, 'component'>,
): void {
  registry[type] = {
    component,
    ...options,
  };
}

/**
 * 블록 컴포넌트 조회
 *
 * @returns 등록된 컴포넌트 또는 undefined
 */
export function getBlockComponent(type: BlockType): BlockComponent | undefined {
  return registry[type]?.component;
}

/**
 * 블록 등록 정보 조회
 */
export function getBlockRegistration(type: BlockType): BlockRegistration | undefined {
  return registry[type];
}

/**
 * 등록된 모든 블록 타입 조회
 */
export function getRegisteredBlockTypes(): BlockType[] {
  return Object.keys(registry) as BlockType[];
}

/**
 * 특정 카테고리의 블록 타입 조회
 */
export function getBlockTypesByCategory(
  category: 'layout' | 'content' | 'data' | 'interactive' | 'media',
): BlockType[] {
  return (Object.entries(registry) as [BlockType, BlockRegistration][])
    .filter(([, reg]) => reg.category === category)
    .map(([type]) => type);
}

/**
 * 블록 등록 여부 확인
 */
export function isBlockRegistered(type: BlockType): boolean {
  return type in registry;
}

/**
 * 레지스트리 초기화 (테스트용)
 */
export function clearRegistry(): void {
  for (const key of Object.keys(registry)) {
    delete registry[key as BlockType];
  }
}

// ============================================================================
// BATCH REGISTRATION HELPER
// ============================================================================

/**
 * 여러 블록을 한번에 등록
 *
 * @example
 * ```typescript
 * registerBlocks({
 *   heading: { component: HeadingBlock, category: 'content' },
 *   text: { component: TextBlock, category: 'content' },
 *   row: { component: RowBlock, category: 'layout' },
 * });
 * ```
 */
export function registerBlocks(
  blocks: Partial<
    Record<
      BlockType,
      {
        component: BlockComponent;
        displayName?: string;
        category?: 'layout' | 'content' | 'data' | 'interactive' | 'media';
      }
    >
  >,
): void {
  for (const [type, config] of Object.entries(blocks)) {
    if (config) {
      registerBlock(type as BlockType, config.component, {
        displayName: config.displayName,
        category: config.category,
      });
    }
  }
}

// ============================================================================
// RENDER HELPER
// ============================================================================

/**
 * 블록을 렌더링하는 헬퍼 함수
 *
 * @returns 렌더링된 React 엘리먼트 또는 null (미등록 블록)
 *
 * @example
 * ```typescript
 * const element = renderBlockFromRegistry(block, { onToolClick });
 * ```
 */
export function renderBlockFromRegistry(
  block: Block,
  context: BlockRendererContext = {},
): React.ReactElement | null {
  const Component = getBlockComponent(block.type);

  if (!Component) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[BlockRegistry] Unknown block type: ${block.type}`);
    }
    return null;
  }

  // renderBlock을 context에 추가하여 재귀 렌더링 지원
  const enhancedContext: BlockRendererContext = {
    ...context,
    renderBlock: (childBlock: Block) => renderBlockFromRegistry(childBlock, context),
  };

  return <Component key={block.id} {...block} {...enhancedContext} />;
}

// ============================================================================
// FALLBACK COMPONENT
// ============================================================================

/**
 * 미등록 블록용 폴백 컴포넌트
 */
export function UnknownBlock({ type, id }: { type: string; id: string }) {
  if (process.env.NODE_ENV === 'development') {
    return (
      <div className="p-4 border border-dashed border-amber-400 bg-amber-50 rounded-md">
        <p className="text-sm text-amber-700">
          Unknown block type: <code className="font-mono">{type}</code>
        </p>
        <p className="text-xs text-amber-600 mt-1">Block ID: {id}</p>
      </div>
    );
  }
  return null;
}
